import { useEffect, useRef, useState } from 'react';
import {
  ArrowBackOutlined,
  LockOutlined,
  ExpandMoreOutlined,
} from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Divider, IconButton, Stack, Typography } from '@mui/material';
import {
  CompactEditButton,
  DefinitionList,
  InformationPage,
  InlineNotice,
  SaveStatus,
  PageSections,
  SectionCard,
  StatusBadge,
  useConfirmation,
  designTokens,
} from '../../../design-system';
import {
  mappingKey,
  mappingCandidates,
  candidateParts,
  cloneMappingRule,
  rulesForProvider,
  validateMappings,
  previewValue,
} from '../model/mappings';
import type { ProfileMappingRule, ProviderProfileMappings, SourcePropertyDefinition } from '../model/types';
import type { IntegrationSettingsPort } from '../port/IntegrationSettingsPort';
import { IntegrationStatusCard } from './IntegrationStatusCard';
import { MappingRuleEditor } from './MappingRuleEditor';
import { MappingRuleSummary } from './MappingRuleSummary';

function ManagedFields({ provider }: { provider: ProviderProfileMappings }) {
  return (
    <Box>
      <Stack spacing={2} divider={<Divider flexItem />}>
        {provider.managedFields.map((field) => (
          <Box key={field.path}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 750 }}>{field.label}</Typography>
            <DefinitionList items={[
              ['외부 필드', <Typography component="code" variant="caption">{field.path}</Typography>],
              ['Heyday 기준', field.source],
              ['관리 이유', field.description],
            ]} />
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

export function ProfileMappingDetail({
  provider,
  sources,
  port,
  fixtureMode,
  canWrite,
  readOnly,
  updatingEnabled,
  enabledNotice,
  onSuccess,
  onEnabledChange,
  onSaved,
  onBack,
}: {
  provider: ProviderProfileMappings;
  sources: SourcePropertyDefinition[];
  port: IntegrationSettingsPort;
  fixtureMode: boolean;
  canWrite: boolean;
  readOnly: boolean;
  updatingEnabled: boolean;
  enabledNotice?: { severity: 'error'; message: string };
  onSuccess: (message: string) => void;
  onEnabledChange: (enabled: boolean) => void;
  onSaved: (provider: ProviderProfileMappings) => void;
  onBack: () => void;
}) {
  const [draft, setDraft] = useState<ProfileMappingRule>();
  const [textState, setTextState] = useState<{ error?: string; dirty: boolean }>({ dirty: false });
  const [saving, setSaving] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [refreshed, setRefreshed] = useState(false);
  const [notice, setNotice] = useState<{ severity: 'error'; message: string }>();
  const pending = useRef(false);
  const editButtonRefs = useRef(new Map<string, HTMLButtonElement>());
  const refreshedNoticeRef = useRef<HTMLDivElement>(null);
  const { confirm, confirmationDialog } = useConfirmation();
  const editable = canWrite && !readOnly;
  const busy = saving || reloading || updatingEnabled;
  const savedRules = rulesForProvider(provider);
  const editedTarget = provider.targetFields.find((target) => target.key === draft?.targetField);
  const savedRule = savedRules.find((rule) => rule.targetField === draft?.targetField);
  const dirty = Boolean(draft && savedRule && (mappingKey([draft]) !== mappingKey([savedRule]) || textState.dirty));
  const nextRules = savedRules.map((rule) => rule.targetField === draft?.targetField ? draft : rule);
  const unfinishedValueIndex = draft ? mappingCandidates(draft).findIndex((candidate) => !candidateParts(candidate).some((part) => part.sourceProperty)) : -1;
  const validationError = textState.error || (draft && unfinishedValueIndex >= 0
    ? `${editedTarget?.label ?? '연결 설정'}의 ${unfinishedValueIndex + 1}번째 값에 직원 속성을 연결해 주세요.`
    : draft ? validateMappings(provider, nextRules) : undefined);

  useEffect(() => {
    if (!dirty && !saving) return;
    const preventUnload = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', preventUnload);
    return () => window.removeEventListener('beforeunload', preventUnload);
  }, [dirty, saving]);

  const closeEditor = (targetField?: string) => {
    setDraft(undefined);
    setTextState({ dirty: false });
    setConflict(false);
    setRefreshed(false);
    setNotice(undefined);
    if (targetField) requestAnimationFrame(() => editButtonRefs.current.get(targetField)?.focus());
  };

  const discardBefore = async (action: () => void) => {
    if (busy || pending.current) return;
    if (dirty && !await confirm({
      title: '저장하지 않은 변경을 취소할까요?',
      description: '편집 중인 필드의 변경 내용이 사라집니다. 마지막으로 저장한 설정은 유지됩니다.',
      confirmLabel: '변경 취소', cancelLabel: '계속 편집', intent: 'discard',
    })) return;
    action();
  };

  const save = async () => {
    if (!editable || !draft || !dirty || validationError || busy || conflict || pending.current) return;
    pending.current = true;
    setSaving(true);
    setNotice(undefined);
    const targetField = draft.targetField;
    const mappings = nextRules.map(cloneMappingRule);
    try {
      const updated = fixtureMode
        ? { ...provider, mappings, version: provider.version + 1, updatedAt: new Date().toISOString() }
        : await port.updateProfileMappings(provider.provider, { mappings, version: provider.version });
      onSaved(updated);
      closeEditor(targetField);
      onSuccess('프로필 매핑 설정을 저장했습니다. 다음 동기화부터 반영됩니다.');
    } catch (reason) {
      const stale = reason instanceof Error && 'status' in reason && reason.status === 409;
      setConflict(stale);
      setNotice({ severity: 'error', message: stale
        ? '다른 관리자가 설정을 변경했습니다. 입력한 내용은 유지됩니다. 최신 설정을 불러와 확인해 주세요.'
        : reason instanceof Error ? reason.message : '프로필 매핑 설정을 저장하지 못했습니다.' });
    } finally {
      pending.current = false;
      setSaving(false);
    }
  };

  const reloadBaseline = async () => {
    if (!editable || busy || pending.current) return;
    pending.current = true;
    setReloading(true);
    try {
      const latest = (await port.getProfileMappingConfiguration()).providers.find((item) => item.provider === provider.provider);
      if (!latest) throw new Error('최신 설정을 찾을 수 없습니다.');
      onSaved(latest);
      setConflict(false);
      setNotice(undefined);
      setRefreshed(true);
      requestAnimationFrame(() => refreshedNoticeRef.current?.focus());
    } catch (reason) {
      setNotice({ severity: 'error', message: reason instanceof Error ? reason.message : '최신 설정을 불러오지 못했습니다.' });
    } finally { pending.current = false; setReloading(false); }
  };

  return (
    <InformationPage
      title={provider.displayName}
      width="wide"
      navigation={<IconButton aria-label="디렉터리 동기화로 돌아가기" onClick={() => { void discardBefore(onBack); }} disabled={busy}><ArrowBackOutlined /></IconButton>}
      utilities={!editable ? <StatusBadge label="조회 전용" tone="neutral" /> : undefined}
      testId="integration-mapping-detail"
    >
      <PageSections>
        {readOnly && <InlineNotice severity="info">다른 사용자로 보는 동안에는 연동 설정을 변경할 수 없습니다.</InlineNotice>}
        {!readOnly && !canWrite && <InlineNotice severity="info">연동 설정을 변경할 권한이 필요합니다.</InlineNotice>}
        {enabledNotice?.severity === 'error' && <InlineNotice severity="error" announcement="polite">{enabledNotice.message}</InlineNotice>}
        {!draft && <IntegrationStatusCard
          providerName={provider.displayName}
          enabled={provider.enabled}
          disabled={!editable || busy || Boolean(draft)}
          description={provider.provider === 'GOOGLE_WORKSPACE'
            ? '직원 계정과 프로필을 동기화합니다. 조직 그룹에는 하위 조직과 겸직 구성원도 포함합니다.'
            : '기존 Slack 사용자의 프로필을 동기화합니다. 계정 생성과 비활성화는 포함하지 않습니다.'}
          onEnabledChange={onEnabledChange}
        />}

        <Box component="section" aria-label="프로필 매핑" data-testid="integration-mapping-card">
          <SectionCard
            title={draft && editedTarget ? `${editedTarget.label} 연결 설정` : '프로필 동기화'}
            help="외부 프로필에 사용할 값과 값이 없을 때의 대체값을 설정합니다. 여러 직원 속성을 조합할 수 있습니다."
            bodyPadding="standard"
            stickyHeader={Boolean(draft) && editable}
            action={editable && draft ? (
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
                <SaveStatus status={{ state: busy ? 'saving' : conflict ? 'conflict' : notice?.severity === 'error' ? 'error' : dirty ? 'pending' : 'idle' }} />
                <Button variant="outlined" color="inherit" disabled={busy} onClick={() => closeEditor(draft.targetField)}>취소</Button>
                <Button variant="contained" disabled={!dirty || Boolean(validationError) || busy || conflict} onClick={() => { void save(); }}>저장</Button>
              </Stack>
            ) : <Typography variant="caption" color="text.secondary">Heyday → {provider.displayName}</Typography>}
          >
            <Stack spacing={2} divider={<Divider flexItem />}>
              {provider.targetFields.map((target) => {
                if (draft && draft.targetField !== target.key) return null;
                const rule = savedRules.find((candidate) => candidate.targetField === target.key);
                if (!rule) return null;
                if (draft?.targetField === target.key && editable) return <Stack key={target.key} spacing={2}>
                  {notice?.severity === 'error' && <InlineNotice severity="error" announcement="polite" action={conflict ? <Button disabled={busy} onClick={() => { void reloadBaseline(); }}>최신 설정 불러오기</Button> : undefined}>{notice.message}</InlineNotice>}
                  {refreshed && <Box ref={refreshedNoticeRef} tabIndex={-1}><InlineNotice severity="info">최신 설정을 불러왔습니다. 현재 저장된 규칙의 예시 결과는 ‘{previewValue(rule, sources) || '값 없음'}’입니다. 입력한 변경 내용을 확인한 뒤 다시 저장해 주세요.</InlineNotice></Box>}
                  {validationError && <InlineNotice severity="warning">{validationError}</InlineNotice>}
                  <MappingRuleEditor target={target} rule={draft} sources={sources} disabled={busy}
                    onChange={(next, state) => { if (!conflict) setNotice(undefined); setDraft(next); setTextState(state); }} />
                </Stack>;
                return <MappingRuleSummary key={target.key} target={target} rule={rule} sources={sources}
                  action={editable ? <CompactEditButton
                    ref={(element) => { if (element) editButtonRefs.current.set(target.key, element); else editButtonRefs.current.delete(target.key); }}
                    aria-label={`${target.label} 매핑 수정`} disabled={busy}
                    onClick={() => { void discardBefore(() => { setDraft(cloneMappingRule(rule)); setTextState({ dirty: false }); setNotice(undefined); setConflict(false); setRefreshed(false); }); }}>수정</CompactEditButton> : undefined} />;
              })}
            </Stack>
          </SectionCard>
        </Box>

        {!draft && provider.managedFields.length > 0 && <SectionCard title={<Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}><LockOutlined fontSize="small" color="action" />자동으로 관리되는 정보</Stack>} showDivider={false}>
          <Accordion disableGutters sx={{ mx: `${designTokens.space.xs}px`, mb: `${designTokens.space.xs}px` }}>
            <AccordionSummary expandIcon={<ExpandMoreOutlined />} aria-controls="integration-managed-fields" id="integration-managed-fields-heading">
              <Typography variant="body2">{provider.managedFields.map((field) => field.label).join(' · ')}</Typography>
            </AccordionSummary>
            <AccordionDetails id="integration-managed-fields"><ManagedFields provider={provider} /></AccordionDetails>
          </Accordion>
        </SectionCard>}
      </PageSections>
      {confirmationDialog}
    </InformationPage>
  );
}
